// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useState } from 'react';

import type {
  EvolveApiBody,
  EvolveApiMethod,
  EvolveApiResponse,
  EvolveApiRoute,
  RouteWithPathParam,
} from '../../ENDPOINTS';
import { extractErrorMessage } from '../extractError';
import type { WithOptional } from '../helper-types';
import { isCanceledApiRequest } from '../isRequestError';
import { type EvolveRoute, useBaseEvolveApi } from '../useBaseEvolveApi';
import { useTrackedRequests } from '../useTrackedRequests';

import type { AdditionalRequestConfig } from './external-types';

export type Opts<Method extends EvolveApiMethod, Route extends EvolveApiRoute<Method>> = {
  /**
   * The default config passed to each api call.
   * To make changes per-call, pass the new config params into the `apiCall`.
   */
  defaultConfig?: WithOptional<AdditionalRequestConfig<Method, Route>, 'url'>;
  /**
   * If `true`, will show a notification for any error received from the backend.
   * The thrown error will be the parsed notification string.
   *
   * If `false`, the thrown error will be the original error object.
   *
   * @remarks Defaults to `false` for `GET` and `PAGINATED_GET`.
   * @remarks Defaults to `true` for `POST`, `PUT`, `PATCH`, and `DELETE`.
   */
  alertOnError?: boolean;
  /**
   * If `true`, will abort any previous requests that are still pending when a new request is made.
   * Only applicable for idempotent calls.
   * @defaultValue false
   */
  abortOnMoreRecent?: boolean;
  /**
   * If `true`, will abort any in-progress requests when the component using this hook unmounts.
   *   * Only applicable for idempotent calls, should be set to `false` if the request data may be used after the component unmounts.
   * @defaultValue false
   */
  abortOnUnmount?: boolean;
};

export type NoBodyApiCallArgs<
  Method extends EvolveApiMethod,
  Route extends EvolveApiRoute<Method>,
> = Route extends RouteWithPathParam
  ? readonly [config: AdditionalRequestConfig<Method, Route>]
  : readonly [config?: AdditionalRequestConfig<Method, Route>];

export type WithBodyApiCallArgs<
  Method extends EvolveApiMethod,
  Route extends EvolveApiRoute<Method>,
> = Route extends RouteWithPathParam
  ? readonly [body: EvolveApiBody<Method, Route>, config: AdditionalRequestConfig<Method, Route>]
  : readonly [body: EvolveApiBody<Method, Route>, config?: AdditionalRequestConfig<Method, Route>];

export type ApiCallReturn<
  Method extends EvolveApiMethod,
  Route extends EvolveApiRoute<Method>,
  ApiCallArgs extends NoBodyApiCallArgs<Method, Route> | WithBodyApiCallArgs<Method, Route>,
> = {
  /**
   * Can be used to manually call the API.
   *
   * Requires the proper URL and query parameters to be passed in.
   */
  apiCall: (...args: ApiCallArgs) => Promise<EvolveApiResponse<Method, Route>>;
  /** The returned data from this API call (if the call has been made). */
  data: EvolveApiResponse<Method, Route> | undefined;
  /** Used to update the default opts after hook instantiation. */
  setDefaultOpts: (opts: Opts<Method, Route>) => void;
  /** Whether an API call is currently in progress. */
  loading: boolean;
  /** Clears out all stored data (usually in prep for a new `apiCall`) */
  reset: () => void;
  /** Any errors returned from the backend. */
  errors: unknown | undefined;
  /** Cancels any in-progress API calls. Calls made afterwards are unaffected. */
  abortAll: (reason: string) => void;
};

export const useWrappedApiCallNoBody = <Method extends EvolveApiMethod, Route extends EvolveApiRoute<Method>>(
  method: 'get' | 'apiDelete',
  url: Route,
  defaultOpts?: Opts<Method, Route>,
): ApiCallReturn<Method, Route, NoBodyApiCallArgs<Method, Route>> => {
  // Frequently, the passed in parameters are recalculated on a rerender
  // so we need to be very careful to not rerender/recalculate when they change.
  const [initialOpts, setDefaultOpts] = useState(defaultOpts);
  const [data, setData] = useState<EvolveApiResponse<Method, Route> | undefined>(undefined);
  const [errors, setErrors] = useState<unknown>();
  const { loading, abortAll, trackRequest } = useTrackedRequests(
    initialOpts?.abortOnUnmount,
    'Component calling useWrappedApiCallNoBody unmounted.',
  );
  const { [method]: apiMethod, showError } = useBaseEvolveApi<
    EvolveApiResponse<Method, Route>,
    EvolveApiBody<Method, Route>
  >();

  const apiCall = useCallback(
    async (...[config]: NoBodyApiCallArgs<Method, Route>): Promise<EvolveApiResponse<Method, Route>> => {
      setErrors(undefined);

      const constructedConfig = {
        ...initialOpts?.defaultConfig,
        ...config,
      } as AdditionalRequestConfig<Method, Route>;
      const requestUrl = config?.url ?? url;
      return trackRequest(
        (signal) =>
          apiMethod(requestUrl, {
            ...constructedConfig,
            signal,
          })
            .then((res) => {
              // Axios only rejects on abort while the request is pending, so an abort that lands after it resolves is caught here.
              signal.throwIfAborted();
              setData(res);
              return res;
            })
            .catch((err) => {
              if (!isCanceledApiRequest(err)) {
                const extractedError = extractErrorMessage(err);
                setErrors(extractedError);
                if (
                  initialOpts?.alertOnError ||
                  // Check false explicitly since default behavior is `true`
                  (method === 'apiDelete' && initialOpts?.alertOnError !== false)
                ) {
                  showError(err);
                  throw extractedError;
                }
              }
              throw err;
            }),
        initialOpts?.abortOnMoreRecent ?? false,
        `More recent call made. ${requestUrl}`,
      );
    },
    [
      apiMethod,
      initialOpts?.defaultConfig,
      initialOpts?.alertOnError,
      initialOpts?.abortOnMoreRecent,
      method,
      showError,
      trackRequest,
      url,
    ],
  );

  const reset = useCallback(() => {
    // DELETEs may mutate data, so we don't abort ongoing requests so that `onDelete` callbacks can still be triggered.
    if (method !== 'apiDelete') {
      abortAll('API call & data reset');
    }
    setData(undefined);
  }, [method, abortAll]);

  return {
    setDefaultOpts,
    apiCall,
    data,
    loading,
    reset,
    errors,
    abortAll,
  };
};

export const useWrappedApiCallWithBody = <Method extends EvolveApiMethod, Route extends EvolveApiRoute<Method>>(
  method: 'post' | 'patch' | 'put',
  url: EvolveRoute,
  defaultOpts?: Opts<Method, Route>,
): ApiCallReturn<Method, Route, WithBodyApiCallArgs<Method, Route>> => {
  const [initialOpts, setDefaultOpts] = useState(defaultOpts);
  const [data, setData] = useState<EvolveApiResponse<Method, Route> | undefined>(undefined);
  const [errors, setErrors] = useState<unknown>();
  const { loading, abortAll, trackRequest } = useTrackedRequests(
    initialOpts?.abortOnUnmount,
    'Component calling useWrappedApiCallWithBody unmounted.',
  );
  const { [method]: apiMethod, showError } = useBaseEvolveApi<
    EvolveApiResponse<Method, Route>,
    EvolveApiBody<Method, Route>
  >();

  const apiCall = useCallback(
    async (...[body, config]: WithBodyApiCallArgs<Method, Route>) => {
      setErrors(undefined);

      const constructedConfig = {
        ...initialOpts?.defaultConfig,
        ...config,
      } as AdditionalRequestConfig<Method, Route>;
      const requestUrl = config?.url ?? url;
      return trackRequest(
        (signal) =>
          apiMethod(requestUrl, body as EvolveApiBody<Method, Route>, {
            ...constructedConfig,
            signal,
          })
            .then((res) => {
              // Axios only rejects on abort while the request is pending, so an abort that lands after it resolves is caught here.
              signal.throwIfAborted();
              setData(res);
              return res;
            })
            .catch((err) => {
              if (!isCanceledApiRequest(err)) {
                const extractedError = extractErrorMessage(err);
                setErrors(extractedError);
                // Check false explicitly since default behavior is `true`
                if (initialOpts?.alertOnError !== false) {
                  showError(err);
                  throw extractedError;
                }
              }
              throw err;
            }),
        initialOpts?.abortOnMoreRecent ?? false,
        `More recent call made. ${requestUrl}`,
      );
    },
    [
      apiMethod,
      initialOpts?.defaultConfig,
      initialOpts?.abortOnMoreRecent,
      initialOpts?.alertOnError,
      showError,
      trackRequest,
      url,
    ],
  );

  // We don't abort ongoing requests when resetting data since API calls with a body can reasonably be expected to mutate data.
  // This makes sure in-flight requests will still trigger `onUpdate` functions in those cases.
  const reset = useCallback(() => setData(undefined), []);

  return {
    setDefaultOpts,
    apiCall,
    data,
    loading,
    reset,
    errors,
    abortAll,
  };
};
